import type { LandingScreenshot } from "#landing-screenshots";

/* eslint-disable tailwindcss/no-restricted-classes -- These radii depict phone hardware and scale with the illustration, independently of app surfaces. */

type LandingDeviceFrameProps = {
  /** Sizes the device; every frame detail scales with this width via container query units. */
  className?: string;
  /** Above-the-fold screenshots load eagerly with high priority; the rest load lazily. */
  priority?: boolean;
  screenshot: LandingScreenshot;
};

function LandingDeviceScreenshotImage({
  priority = false,
  screenshot,
}: Pick<LandingDeviceFrameProps, "priority" | "screenshot">) {
  return (
    <img
      alt={screenshot.alt}
      className="block h-auto w-full select-none"
      decoding="async"
      draggable={false}
      fetchPriority={priority ? "high" : "auto"}
      height={screenshot.height}
      loading={priority ? "eager" : "lazy"}
      src={screenshot.src}
      width={screenshot.width}
    />
  );
}

/**
 * iPhone 17 hardware drawn around a simulator screenshot. The capture already contains the status
 * bar and the Dynamic Island, so the frame supplies only the bezel, buttons and rounded corners.
 */
export function LandingIphoneFrame({
  className = "",
  priority,
  screenshot,
}: LandingDeviceFrameProps) {
  return (
    <div className={`@container ${className}`}>
      <div className="relative rounded-[17cqw] bg-linear-145 from-landing-iphone-highlight via-landing-iphone-body to-landing-iphone-body p-[0.9cqw] shadow-2xl shadow-black/30">
        <span className="absolute top-[25cqw] left-[-0.55cqw] h-[7cqw] w-[0.9cqw] rounded-l-[0.5cqw] bg-landing-iphone-button" />
        <span className="absolute top-[38cqw] left-[-0.55cqw] h-[13cqw] w-[0.9cqw] rounded-l-[0.5cqw] bg-landing-iphone-button" />
        <span className="absolute top-[54cqw] left-[-0.55cqw] h-[13cqw] w-[0.9cqw] rounded-l-[0.5cqw] bg-landing-iphone-button" />
        <span className="absolute top-[44cqw] right-[-0.55cqw] h-[20cqw] w-[0.9cqw] rounded-r-[0.5cqw] bg-landing-iphone-button" />
        <div className="rounded-[16.1cqw] bg-black p-[3cqw]">
          <div className="relative overflow-hidden rounded-[13.2cqw] bg-black">
            <LandingDeviceScreenshotImage priority={priority} screenshot={screenshot} />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * A Pixel-style Android phone drawn around an emulator screenshot: flatter corners, an even
 * bezel and a centered punch-hole camera.
 */
export function LandingAndroidFrame({
  className = "",
  priority,
  screenshot,
}: LandingDeviceFrameProps) {
  return (
    <div className={`@container ${className}`}>
      <div className="relative rounded-[12cqw] bg-linear-160 from-landing-android-highlight via-landing-android-body to-landing-android-body p-[0.9cqw] shadow-2xl shadow-black/30">
        <span className="absolute top-[36cqw] right-[-0.55cqw] h-[11cqw] w-[0.9cqw] rounded-r-[0.5cqw] bg-landing-android-button" />
        <span className="absolute top-[52cqw] right-[-0.55cqw] h-[22cqw] w-[0.9cqw] rounded-r-[0.5cqw] bg-landing-android-button" />
        <div className="rounded-[11.1cqw] bg-black p-[2.8cqw]">
          <div className="relative overflow-hidden rounded-[8.6cqw] bg-black">
            <LandingDeviceScreenshotImage priority={priority} screenshot={screenshot} />
            <span className="absolute top-[2.6cqw] left-1/2 size-[3.6cqw] -translate-x-1/2 rounded-full bg-black ring-[0.5cqw] ring-landing-android-camera-ring" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function LandingDeviceFrame(props: LandingDeviceFrameProps) {
  return props.screenshot.platform === "ios" ? (
    <LandingIphoneFrame {...props} />
  ) : (
    <LandingAndroidFrame {...props} />
  );
}

/* eslint-enable tailwindcss/no-restricted-classes -- The hardware radii above end with the device frames. */
